/**
 * Lowest viewport Y the secondary panel may occupy without covering sticky
 * page chrome above the main content (e.g. BoxContainer fixed / FilterContainer).
 *
 * Ignores `fixed` full-height shells (sidebar/layout) and only considers sticky
 * bars that still intersect the upper viewport.
 */
export const getSecondaryPanelStickyCeiling = (
  contentNode: HTMLElement,
  {
    minGap,
    ignoreNode = null,
    viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 0,
  }: {
    minGap: number;
    ignoreNode?: HTMLElement | null;
    viewportHeight?: number;
  },
): number => {
  let ceiling = minGap;
  let current: HTMLElement | null = contentNode;
  const maxHeaderHeight = viewportHeight > 0 ? viewportHeight * 0.5 : Number.POSITIVE_INFINITY;

  while (current && current !== document.documentElement) {
    let sibling = current.previousElementSibling as HTMLElement | null;

    while (sibling) {
      const shouldIgnore =
        Boolean(ignoreNode) && (sibling === ignoreNode || sibling.contains(ignoreNode));

      if (!shouldIgnore) {
        const position = window.getComputedStyle(sibling).position;
        if (position === 'sticky') {
          const rect = sibling.getBoundingClientRect();
          const isReasonableHeader = rect.height > 0 && rect.height < maxHeaderHeight;
          const isInUpperViewport = rect.bottom > 0 && rect.top < viewportHeight;

          if (isReasonableHeader && isInUpperViewport) {
            // Use the chrome's own bottom (not bottom + minGap) so the panel aligns
            // to the content top whenever the content already sits below the sticky
            // bar. Adding minGap here overshoots that top when the content starts
            // within minGap of the bar, pushing the panel below the content.
            ceiling = Math.max(ceiling, rect.bottom);
          }
        }
      }

      sibling = sibling.previousElementSibling as HTMLElement | null;
    }

    current = current.parentElement;
  }

  return ceiling;
};
